import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { mapProfile } from "@/lib/adapt";
import { useAuth } from "@/context/AuthContext";
import { useCelebrate } from "@/context/MatchCelebration";
import { EtiquetteNote } from "@/components/EtiquetteNote";
import { Icebreakers } from "@/components/Icebreakers";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Heart, Send, Inbox, Archive, MapPin, ChevronRight, BadgeCheck, Lock, Unlock, ArrowLeftRight, Mail, Phone, MessageCircle, Sparkles, X, MessageCircleQuestion } from "lucide-react";
import QADialog from "@/components/QADialog";
import { CompareDialog } from "@/components/CompareDialog";

const TABS = [
  { key: "matches", label: "Matches", icon: Heart },
  { key: "received", label: "Received", icon: Inbox },
  { key: "sent", label: "Sent", icon: Send },
  { key: "archived", label: "Archived", icon: Archive },
];

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return ""; } };

function RowHeader({ profile, meta }) {
  const navigate = useNavigate();
  const initials = (profile.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");
  return (
    <button onClick={() => navigate(`/profiles/${profile.id}`)} className="flex w-full items-center gap-4 text-left" data-testid={`match-header-${profile.id}`}>
      <Avatar className="h-16 w-16 border-2 border-emerald-100">
        {profile.photo && <AvatarImage src={profile.photo} alt="" className="object-cover" />}
        <AvatarFallback className="bg-emerald-800 font-serif text-lg text-amber-200">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <h3 className="truncate font-serif text-xl font-bold text-emerald-950">{profile.name}{profile.age ? `, ${profile.age}` : ""}</h3>
          {profile.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-600" />}
        </div>
        <p className="flex items-center gap-1 truncate text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {[profile.city, profile.country].filter(Boolean).join(", ") || "—"}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {typeof profile.match_percent === "number" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-mint px-2.5 py-0.5 text-xs font-semibold text-emerald-800"><Sparkles className="h-3 w-3 text-amber-500" /> {profile.match_percent}% match</span>
          )}
          {meta && <span className="text-xs text-slate-400">{meta}</span>}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
    </button>
  );
}

function ContactDialog({ open, onOpenChange, profile, contact }) {
  const items = [
    contact?.email && ["Email", contact.email, Mail],
    contact?.phone && ["Phone", contact.phone, Phone],
    contact?.whatsapp && ["WhatsApp", contact.whatsapp, MessageCircle],
  ].filter(Boolean);
  const wali = profile?.wali;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-serif text-2xl text-emerald-900"><Unlock className="h-5 w-5 text-emerald-600" /> Contact details</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">You matched with {profile?.firstName || profile?.name}. Reach out respectfully.</p>
        <div className="space-y-2">
          {items.length === 0 && <p className="rounded-xl bg-mint p-4 text-sm text-slate-600">This member hasn't shared direct contact details yet.</p>}
          {items.map(([label, value, Icon]) => (
            <div key={label} className="flex items-center gap-3 rounded-xl border border-emerald-900/10 bg-white p-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-mint text-emerald-700"><Icon className="h-4 w-4" /></span>
              <div className="min-w-0"><p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p><p className="truncate text-sm font-medium text-slate-800">{value}</p></div>
            </div>
          ))}
          <EtiquetteNote wali={wali} className="mt-1" />
          <div className="mt-1 border-t border-emerald-900/10 pt-3"><Icebreakers profile={profile} /></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MatchCard({ match, me, index, isPremium, isAssistant }) {
  const navigate = useNavigate();
  const [showContact, setShowContact] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showQA, setShowQA] = useState(false);
  const profile = mapProfile(match.user);
  if (!profile) return null;
  return (
    <div className="animate-fade-up rounded-3xl border border-emerald-900/10 bg-white p-4 shadow-sm sm:p-5" style={{ animationDelay: `${index * 60}ms` }} data-testid={`match-card-${profile.id}`}>
      <RowHeader profile={profile} meta={match.createdAt ? `Matched ${fmtDate(match.createdAt)}` : ""} />
      <div className="mt-4 flex gap-2">
        <button onClick={() => navigate(`/profiles/${profile.id}`)} data-testid={`match-view-profile-${profile.id}`} className="flex-1 rounded-xl bg-mint px-4 py-2.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100">View Profile</button>
        {isPremium ? (
          <button onClick={() => setShowContact(true)} data-testid={`match-view-contact-${profile.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-900"><Unlock className="h-4 w-4" /> View Contact</button>
        ) : (
          <button onClick={() => navigate("/premium")} data-testid={`match-contact-locked-${profile.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-600"><Lock className="h-4 w-4" /> Unlock Contact</button>
        )}
      </div>
      {!isPremium && <p className="mt-2 text-center text-xs text-slate-500" data-testid={`match-contact-premium-note-${profile.id}`}>Contact details are a Premium feature. Upgrade to reach out.</p>}
      {!isAssistant && <button onClick={() => setShowCompare(true)} data-testid={`match-compare-${profile.id}`} className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800 hover:bg-amber-100"><ArrowLeftRight className="h-4 w-4" /> Compare Profiles</button>}
      <button onClick={() => setShowQA(true)} data-testid={`match-qa-${profile.id}`} className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-800/20 bg-mint/50 px-4 py-2.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"><MessageCircleQuestion className="h-4 w-4" /> Questions &amp; Answers</button>
      {isPremium && <ContactDialog open={showContact} onOpenChange={setShowContact} profile={profile} contact={match.contact} />}
      <CompareDialog open={showCompare} onOpenChange={setShowCompare} me={me} them={profile} onAsk={() => setShowQA(true)} />
      <QADialog open={showQA} onOpenChange={setShowQA} otherId={profile.id} otherName={profile.firstName || profile.name} />
    </div>
  );
}

function InterestCard({ item, tab, onAccept, onDecline, index }) {
  const navigate = useNavigate();
  const profile = mapProfile(item.user);
  const statusColor = item.status === "mutual" || item.status === "accepted" ? "bg-emerald-600" : "bg-amber-500";
  return (
    <div className="animate-fade-up rounded-3xl border border-emerald-900/10 bg-white p-4 shadow-sm sm:p-5" style={{ animationDelay: `${index * 60}ms` }} data-testid={`interest-card-${profile.id}`}>
      <RowHeader profile={profile} meta={item.createdAt ? fmtDate(item.createdAt) : ""} />
      <div className="mt-4 flex items-center gap-2">
        {tab === "received" ? (
          <>
            <button onClick={() => onDecline(profile)} data-testid={`interest-decline-${profile.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50"><X className="h-4 w-4" /> Decline</button>
            <button onClick={() => onAccept(profile)} data-testid={`interest-accept-${profile.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-900"><Heart className="h-4 w-4 fill-current" /> Accept</button>
          </>
        ) : (
          <>
            <button onClick={() => navigate(`/profiles/${profile.id}`)} data-testid={`interest-view-profile-${profile.id}`} className="flex-1 rounded-xl bg-mint px-4 py-2.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100">View Profile</button>
            {item.status && <span className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize text-white ${statusColor}`}>{item.status}</span>}
          </>
        )}
      </div>
    </div>
  );
}

export default function Matches() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const celebrate = useCelebrate();
  const me = user?.profile;
  const isPremium = !!user?.is_premium;
  const sentCount = Number(user?.raw?.interestsCount ?? 0);
  const [tab, setTab] = useState("matches");
  const [data, setData] = useState({ matches: null, received: null, sent: null, archived: null });

  const loadAll = async () => {
    const jobs = {
      matches: api.get("/matches").then((r) => (r.data.matches || [])).catch(() => []),
      received: api.get("/interests/received").then((r) => (r.data.items || [])).catch(() => []),
      sent: api.get("/interests/sent").then((r) => (r.data.items || [])).catch(() => []),
      archived: api.get("/interests/archived").then((r) => (r.data.items || [])).catch(() => []),
    };
    const [matches, received, sent, archived] = await Promise.all([jobs.matches, jobs.received, jobs.sent, jobs.archived]);
    setData({ matches, received, sent, archived });
  };

  useEffect(() => { loadAll(); }, []);

  const accept = async (profile) => {
    try {
      const { data: r } = await api.post("/actions", { targetId: profile.id, action: "interested" });
      if (r.matched) celebrate(profile);
      else toast.success("Interest sent");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const decline = async (profile) => {
    try {
      await api.post("/actions", { targetId: profile.id, action: "not_interested" });
      toast.success("Politely declined");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const counts = {
    matches: data.matches?.length,
    received: data.received?.length,
    sent: data.sent?.length,
    archived: data.archived?.length,
  };

  const current = data[tab];

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-400 to-emerald-700 text-white shadow-sm"><Heart className="h-6 w-6 fill-current" /></div>
        <div>
          <h1 className="font-serif text-3xl font-bold text-emerald-950 sm:text-4xl">My Matches</h1>
          <p className="text-sm text-slate-500">Mutual interest unlocks shared contact details</p>
        </div>
      </div>

      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          const n = counts[key];
          return (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-emerald-800 text-white shadow" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>
              <Icon className="h-4 w-4" /> {label}
              {typeof n === "number" && n > 0 && <span className={`ml-0.5 rounded-full px-1.5 text-xs ${active ? "bg-white/25" : "bg-mint text-emerald-800"}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      {current === null ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-3xl" />)}</div>
      ) : current.length === 0 ? (
        tab === "sent" && sentCount > 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-emerald-300 bg-white py-16 text-center" data-testid="sent-syncing">
            <Send className="mb-3 h-9 w-9 text-amber-500" />
            <p className="max-w-sm text-slate-500">You've sent {sentCount} interest{sentCount === 1 ? "" : "s"}. The list isn't available on the web for your account yet.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-emerald-300 bg-white py-16 text-center" data-testid={`${tab}-empty`}>
            <Lock className="mb-3 h-9 w-9 text-amber-500" />
            <p className="max-w-sm text-slate-500">
              {tab === "matches" && "No mutual matches yet. When you and someone both express interest, you'll match here."}
              {tab === "received" && "No one has expressed interest yet."}
              {tab === "sent" && "You haven't sent any interests yet. Head to Discover to start."}
              {tab === "archived" && "No archived profiles."}
            </p>
          </div>
        )
      ) : (
        <div className="space-y-4" data-testid={`${tab}-list`}>
          {tab === "matches"
            ? current.map((m, i) => <MatchCard key={m.matchId || i} match={m} me={me} index={i} isPremium={isPremium} isAssistant={!!user?.is_assistant} />)
            : current.map((it, i) => <InterestCard key={it.id || i} item={it} tab={tab} onAccept={accept} onDecline={decline} index={i} />)}
        </div>
      )}
    </div>
  );
}
